import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Star, User, Lock, MessageSquare } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';

interface ReviewFormProps {
  locationId: string;
  locationName: string;
  onSubmit?: (review: ReviewData) => void;
  onCancel?: () => void;
}

interface ReviewData {
  rating: number;
  comment: string;
  categories: {
    cleanliness?: number;
    heat_quality?: number;
    atmosphere?: number;
    arrival_experience?: number;
    privacy_comfort?: number;
    communication?: number;
    value?: number;
  };
}

const ReviewForm: React.FC<ReviewFormProps> = ({ 
  locationId, 
  locationName, 
  onSubmit, 
  onCancel 
}) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [categories, setCategories] = useState<ReviewData['categories']>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStarClick = (starRating: number) => {
    setRating(starRating);
  };

  const handleCategoryRating = (category: string, value: number) => {
    setCategories(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      const reviewData: ReviewData = {
        rating,
        comment,
        categories
      };
      
      if (onSubmit) {
        await onSubmit(reviewData);
      }
      
      // Reset form
      setRating(0);
      setComment('');
      setCategories({});
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // If user is not logged in, show login prompt
  if (!user) {
    return (
      <Card className="border border-gray-200 bg-white">
        <CardHeader>
          <CardTitle className="flex items-center text-sauna-navy">
            <MessageSquare className="w-5 h-5 mr-2 text-sauna-orange" />
            Write a Review
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center py-8">
            <Lock className="w-12 h-12 mx-auto text-sauna-orange mb-4" />
            <h3 className="text-lg font-semibold text-sauna-navy mb-2">
              Login Required
            </h3>
            <p className="text-gray-600 mb-6">
              Please login or create an account to write a review for {locationName}.
            </p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-sauna-orange hover:bg-sauna-orange/90 text-white">
                  <User className="w-4 h-4 mr-2" />
                  Login to Review
                </Button>
              </Link>
              <Link to="/signup">
                <Button variant="outline" className="w-full border-sauna-orange text-sauna-orange hover:bg-sauna-orange hover:text-white">
                  Create Account
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-gray-200 bg-white">
      <CardHeader>
        <CardTitle className="flex items-center text-sauna-navy">
          <MessageSquare className="w-5 h-5 mr-2 text-sauna-orange" />
          Write a Review for {locationName}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Overall Rating */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-sauna-navy">
              Overall Rating *
            </label>
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleStarClick(star)}
                  className="focus:outline-none"
                >
                  <Star
                    className={`w-6 h-6 ${
                      star <= rating
                        ? 'text-sauna-orange fill-sauna-orange'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-500">
                {rating > 0 ? `${rating} star${rating > 1 ? 's' : ''}` : 'Select rating'}
              </span>
            </div>
          </div>

          {/* Category Ratings */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-sauna-navy">
              Rate by Category
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: 'cleanliness', label: 'Cleanliness', description: 'Was the sauna and plunge area clean and well-maintained?' },
                { key: 'heat_quality', label: 'Heat & Chill Quality', description: 'Was the sauna hot enough, and the plunge cold and refreshing?' },
                { key: 'atmosphere', label: 'Atmosphere', description: 'How did the setting feel — calm, scenic, and enjoyable?' },
                { key: 'arrival_experience', label: 'Arrival Experience', description: 'Was it easy to access and begin your session smoothly?' },
                { key: 'privacy_comfort', label: 'Privacy & Comfort', description: 'Did you feel relaxed and undisturbed during your session?' },
                { key: 'communication', label: 'Communication', description: 'Was info provided clearly before or during your visit?' },
                { key: 'value', label: 'Value for Money', description: 'Did the experience feel worth the price paid?' }
              ].map((category) => (
                <div key={category.key} className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600">
                    {category.label}
                  </label>
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleCategoryRating(category.key, star)}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`w-4 h-4 ${
                            star <= (categories[category.key as keyof typeof categories] || 0)
                              ? 'text-sauna-orange fill-sauna-orange'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">{category.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Review Comment */}
          <div className="space-y-3">
            <label htmlFor="comment" className="block text-sm font-medium text-sauna-navy">
              Your Review *
            </label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience with this sauna..."
              className="min-h-[120px] border-gray-200 focus:border-sauna-orange focus:ring-sauna-orange/20"
              required
            />
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!rating || !comment.trim() || isSubmitting}
              className="bg-sauna-orange hover:bg-sauna-orange/90 text-white"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ReviewForm; 